import React, { useContext, useEffect, useMemo, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Layout from '../../components/layout/layout'
import Seo, { breadcrumbJsonLd, organizationJsonLd } from '../../components/Seo/Seo'
import { LanguageContext } from '../../components/LanguageSwitcher/LanguageContext'
import useEntryMotion from '../../components/motion/useEntryMotion'
import checkicon from '../../images/checkicon.svg'
import track from '../../lib/track'
import leadAttribution from '../../lib/attribution'
import './aiReadiness.scss'

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/myzkobag'

/**
 * A five-question readiness check.
 *
 * It is a lead magnet, but an honest one: the score comes from the visitor's
 * own answers and the advice attached to each band is advice we would actually
 * give. The submitted enquiry carries every answer, so the team opens a lead
 * that already explains the situation instead of "hi, we need AI".
 */
const AiReadiness = () => {
  const { translations } = useContext(LanguageContext)
  const animateIn = useEntryMotion()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // score: how much of the groundwork for an ML project is already in place.
  const QUESTIONS = useMemo(
    () => [
      {
        id: 'goal',
        question: translations.readyQ1,
        options: [
          { label: translations.readyQ1a1, score: 3 },
          { label: translations.readyQ1a2, score: 3 },
          { label: translations.readyQ1a3, score: 3 },
          { label: translations.readyQ1a4, score: 2 },
          { label: translations.readyQ1a5, score: 0 },
        ],
      },
      {
        id: 'data',
        question: translations.readyQ2,
        options: [
          { label: translations.readyQ2a1, score: 3 },
          { label: translations.readyQ2a2, score: 2 },
          { label: translations.readyQ2a3, score: 1 },
          { label: translations.readyQ2a4, score: 0 },
        ],
      },
      {
        id: 'metric',
        question: translations.readyQ3,
        options: [
          { label: translations.readyQ3a1, score: 3 },
          { label: translations.readyQ3a2, score: 2 },
          { label: translations.readyQ3a3, score: 0 },
        ],
      },
      {
        id: 'owner',
        question: translations.readyQ4,
        options: [
          { label: translations.readyQ4a1, score: 3 },
          { label: translations.readyQ4a2, score: 3 },
          { label: translations.readyQ4a3, score: 1 },
          { label: translations.readyQ4a4, score: 0 },
        ],
      },
      {
        id: 'timing',
        question: translations.readyQ5,
        options: [
          { label: translations.readyQ5a1, score: 3 },
          { label: translations.readyQ5a2, score: 2 },
          { label: translations.readyQ5a3, score: 1 },
        ],
      },
    ],
    [translations]
  )

  const MAX_SCORE = QUESTIONS.reduce(
    (total, q) => total + Math.max(...q.options.map((o) => o.score)),
    0
  )

  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState({})
  const [lead, setLead] = useState({ name: '', email: '', company: '' })
  const [status, setStatus] = useState('idle') // idle | sending | sent | error

  const done = step >= QUESTIONS.length
  const score = Object.values(answers).reduce((total, a) => total + a.score, 0)

  const band = useMemo(() => {
    const ratio = score / MAX_SCORE
    if (ratio >= 0.72) {
      return {
        key: 'ready',
        title: translations.readyBand1Title,
        text: translations.readyBand1Text,
        steps: [translations.readyBand1Step1, translations.readyBand1Step2, translations.readyBand1Step3],
      }
    }
    if (ratio >= 0.4) {
      return {
        key: 'almost',
        title: translations.readyBand2Title,
        text: translations.readyBand2Text,
        steps: [translations.readyBand2Step1, translations.readyBand2Step2, translations.readyBand2Step3],
      }
    }
    return {
      key: 'early',
      title: translations.readyBand3Title,
      text: translations.readyBand3Text,
      steps: [translations.readyBand3Step1, translations.readyBand3Step2, translations.readyBand3Step3],
    }
  }, [score, MAX_SCORE, translations])

  const choose = (option) => {
    const q = QUESTIONS[step]
    setAnswers((prev) => ({ ...prev, [q.id]: { question: q.question, answer: option.label, score: option.score } }))
    setStep((s) => s + 1)
  }

  const restart = () => {
    setAnswers({})
    setStep(0)
    setStatus('idle')
  }

  const submit = async (event) => {
    event.preventDefault()
    if (status === 'sending') return
    setStatus('sending')

    // The team receives the whole picture, not just an email address.
    const summary = QUESTIONS.map(
      (q) => `${q.question}\n  → ${(answers[q.id] && answers[q.id].answer) || '—'}`
    ).join('\n\n')

    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          _subject: `AI readiness result (${score}/${MAX_SCORE} — ${band.key}) from ${lead.name || 'the website'}`,
          name: lead.name,
          email: lead.email,
          company: lead.company,
          score: `${score} / ${MAX_SCORE}`,
          result: band.title,
          message: summary,
          source: 'AI readiness assessment',
          ...leadAttribution(),
        }),
      })
      setStatus(res.ok ? 'sent' : 'error')
      if (res.ok) track('readiness_submitted', { band: band.key })
    } catch (err) {
      setStatus('error')
    }
  }

  const progress = Math.round((Math.min(step, QUESTIONS.length) / QUESTIONS.length) * 100)

  return (
    <Layout>
      <Seo
        title={translations.readySeoTitle}
        description={translations.readySeoDescription}
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'AI readiness assessment', path: '/ai-readiness' },
          ]),
        ]}
      />

      <div className='ready-page'>
        <div className='ready-content'>
          <p className='ready-kicker'>{translations.readyKicker}</p>
          <h1 className='ready-title'>{translations.readyTitle}</h1>
          <p className='ready-sub'>{translations.readySub}</p>

          <div className='ready-card'>
            <div className='ready-progress'>
              <div className='ready-progress-bar'>
                <motion.span animate={{ width: `${progress}%` }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} />
              </div>
              <p className='ready-progress-label'>
                {done
                  ? translations.readyDoneLabel
                  : `${translations.readyStepLabel} ${step + 1} / ${QUESTIONS.length}`}
              </p>
            </div>

            <AnimatePresence mode='wait'>
              {!done && (
                <motion.div
                  key={step}
                  initial={animateIn ? { opacity: 0, x: 24 } : false}
                  animate={{ opacity: 1, x: 0 }}
                  exit={animateIn ? { opacity: 0, x: -24 } : undefined}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                  <h2 className='ready-question'>{QUESTIONS[step].question}</h2>
                  <div className='ready-options'>
                    {QUESTIONS[step].options.map((option) => (
                      <button key={option.label} className='ready-option' onClick={() => choose(option)}>
                        <span>{option.label}</span>
                        <span className='ready-option-arrow' aria-hidden='true'>→</span>
                      </button>
                    ))}
                  </div>
                  {step > 0 && (
                    <button className='ready-back' onClick={() => setStep((s) => s - 1)}>
                      ← {translations.readyBack}
                    </button>
                  )}
                </motion.div>
              )}

              {done && (
                <motion.div
                  key='result'
                  initial={animateIn ? { opacity: 0, y: 20 } : false}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className='ready-score-row'>
                    <div className={`ready-score ready-score-${band.key}`}>
                      <span className='ready-score-value'>{score}</span>
                      <span className='ready-score-max'>/ {MAX_SCORE}</span>
                    </div>
                    <div>
                      <h2 className='ready-result-title'>{band.title}</h2>
                      <p className='ready-result-text'>{band.text}</p>
                    </div>
                  </div>

                  <p className='ready-steps-label'>{translations.readyStepsLabel}</p>
                  <ul className='ready-steps'>
                    {band.steps.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>

                  {status === 'sent' ? (
                    <div className='ready-sent'>
                      <img src={checkicon} alt='' width='40' height='40' />
                      <div>
                        <h3>{translations.readySentTitle}</h3>
                        <p>{translations.readySentText}</p>
                      </div>
                    </div>
                  ) : (
                    <form className='ready-form' onSubmit={submit}>
                      <p className='ready-form-title'>{translations.readyFormTitle}</p>
                      <p className='ready-form-sub'>{translations.readyFormSub}</p>

                      <div className='ready-form-row'>
                        <label>
                          <span>{translations.quoteName}</span>
                          <input
                            type='text'
                            value={lead.name}
                            autoComplete='name'
                            required
                            onChange={(e) => setLead({ ...lead, name: e.target.value })}
                          />
                        </label>
                        <label>
                          <span>{translations.quoteEmail}</span>
                          <input
                            type='email'
                            value={lead.email}
                            autoComplete='email'
                            required
                            onChange={(e) => setLead({ ...lead, email: e.target.value })}
                          />
                        </label>
                      </div>
                      <label className='ready-form-full'>
                        <span>{translations.readyCompany}</span>
                        <input
                          type='text'
                          value={lead.company}
                          autoComplete='organization'
                          onChange={(e) => setLead({ ...lead, company: e.target.value })}
                        />
                      </label>

                      <button type='submit' className='ready-submit' disabled={status === 'sending'}>
                        {status === 'sending' ? translations.quoteSending : translations.readySubmit}
                      </button>

                      {status === 'error' && <p className='ready-error'>{translations.readyError}</p>}
                      <p className='ready-note'>{translations.quoteNote}</p>
                    </form>
                  )}

                  <button className='ready-back ready-restart' onClick={restart}>
                    ↻ {translations.readyRestart}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </Layout>
  )
}

export default AiReadiness
